import { createMutation } from "@tanstack/solid-query";
import { Send, Trash2, X } from "lucide-solid";
import { type Component, createSignal, For, Show, onMount } from "solid-js";
import { ai } from "../../lib/ai";
import { ApiError } from "../../lib/api";
import type { ChatMessage as Msg } from "../../lib/types";
import { Button } from "../ui/button";
import {
  appendMessage,
  chatMessages,
  chatSending,
  clearChat,
  setChatOpen,
  setChatSending,
} from "./chat-store";
import { ChatMessage } from "./ChatMessage";

export const ChatPanel: Component = () => {
  const [input, setInput] = createSignal("");
  const [error, setError] = createSignal<string | null>(null);
  let scrollRef: HTMLDivElement | undefined;

  const scrollToBottom = () => {
    if (scrollRef) {
      queueMicrotask(() => {
        scrollRef!.scrollTop = scrollRef!.scrollHeight;
      });
    }
  };

  onMount(scrollToBottom);

  const send = createMutation(() => ({
    mutationFn: async (text: string) => {
      const userMsg: Msg = { role: "user", content: text };
      appendMessage(userMsg);
      scrollToBottom();
      // Build messages array WITHOUT local-only ts/tool_calls metadata.
      const history = chatMessages().map((m) => ({
        role: m.role,
        content: m.content,
        ...(m.name ? { name: m.name } : {}),
      }));
      return ai.chat({ messages: history });
    },
    onMutate: () => {
      setError(null);
      setChatSending(true);
    },
    onSuccess: (resp) => {
      appendMessage({
        role: "assistant",
        content: resp.message.content,
        tool_calls: resp.tool_calls,
      });
      scrollToBottom();
    },
    onError: (e: Error) => {
      const msg = e instanceof ApiError ? `${e.code}: ${e.message}` : e.message;
      setError(msg);
    },
    onSettled: () => setChatSending(false),
  }));

  const submit = (e?: Event) => {
    e?.preventDefault();
    const text = input().trim();
    if (!text || chatSending()) return;
    setInput("");
    send.mutate(text);
  };

  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div class="fixed bottom-4 right-4 z-50 w-[360px] max-w-[calc(100vw-2rem)] h-[560px] max-h-[calc(100vh-2rem)] bg-card border rounded-lg shadow-xl flex flex-col">
      {/* Header */}
      <div class="flex items-center justify-between px-3 py-2 border-b">
        <div class="font-medium text-sm">Booking Assistant</div>
        <div class="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            class="h-7 w-7"
            title="Clear history"
            onClick={() => clearChat()}
          >
            <Trash2 class="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            class="h-7 w-7"
            title="Close"
            onClick={() => setChatOpen(false)}
          >
            <X class="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} class="flex-1 overflow-y-auto p-3 space-y-3">
        <Show
          when={chatMessages().length > 0}
          fallback={
            <div class="text-sm text-muted-foreground text-center py-8">
              <p class="mb-2">Xin chào! Mình có thể giúp bạn:</p>
              <ul class="text-xs space-y-1 text-left inline-block">
                <li>• "Liệt kê tất cả groups"</li>
                <li>• "Tạo item tên Show A với 100 slot"</li>
                <li>• "Book item id ... cho user"</li>
                <li>• "Set flow chain cho group X"</li>
              </ul>
            </div>
          }
        >
          <For each={chatMessages()}>{(m) => <ChatMessage message={m} />}</For>
        </Show>
        <Show when={chatSending()}>
          <div class="text-xs text-muted-foreground italic">đang nghĩ…</div>
        </Show>
      </div>

      {/* Error */}
      <Show when={error()}>
        <div class="px-3 py-2 text-xs bg-destructive/10 text-destructive border-t">{error()}</div>
      </Show>

      {/* Input */}
      <form onSubmit={submit} class="border-t p-2 flex items-end gap-2">
        <textarea
          class="flex-1 resize-none rounded-md border bg-background px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
          rows="2"
          placeholder="Hỏi gì đó..."
          value={input()}
          onInput={(e) => setInput(e.currentTarget.value)}
          onKeyDown={onKey}
          disabled={chatSending()}
        />
        <Button type="submit" size="icon" class="h-9 w-9" disabled={chatSending() || !input().trim()}>
          <Send class="h-4 w-4" />
        </Button>
      </form>
    </div>
  );
};
